import { Link, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSeo } from "../hooks/useSeo";
import { buildCanonicalUrl } from "../seo/config";
import type { LayoutOutletContext } from "../layout/types";

type DetailItem = {
  label: string;
  value: string;
};

type WinnersTable = {
  columns: string[];
  rows: string[][];
};

type GalleryItem = {
  src: string;
  alt: string;
  caption: string;
};

type QuizFinalContent = {
  heading?: string;
  intro?: string;
  buttonAlt?: string;
  cornerGraphicAlt?: string;
  event?: {
    details?: DetailItem[];
    guestsHeading?: string;
    guests?: string[];
  };
  winners?: {
    heading?: string;
    table?: WinnersTable;
  };
  video?: {
    heading?: string;
    youtubeId?: string;
  };
  gallery?: GalleryItem[];
};

const NON_BREAKING_GUEST_PHRASES = ["呂志宏先生"];

export const TheQuizFinal = () => {
  const { t } = useTranslation();
  const { locale } = useOutletContext<LayoutOutletContext>();
  const pageContent = (t("pages.theQuizFinal", {
    returnObjects: true,
    defaultValue: {},
  }) ?? {}) as QuizFinalContent & { title?: string };
  const pageTitle = pageContent.title ?? t("pages.theQuizFinal.title");
  const siteTitle = t("site.title");
  const intro = pageContent.intro ?? "";

  useSeo({
    title: `${pageTitle} | ${siteTitle}`,
    description: intro,
    canonical: buildCanonicalUrl("event-details/", locale),
    locale,
  });

  const isEnglish = locale === "en";

  const chinesePageHref = "/event-details/";
  const englishButtonImage = `${import.meta.env.BASE_URL}assets/quiz-final/final-viewchi-en.png`;
  const englishButtonAlt =
    pageContent.buttonAlt ?? "View the Chinese version of this page";
  const cornerGraphicSrc = `${import.meta.env.BASE_URL}assets/quiz-final/quiz-final-picture.png`;
  const cornerGraphicAlt =
    pageContent.cornerGraphicAlt ?? "Quiz final highlight graphic";

  const eventDetails = Array.isArray(pageContent.event?.details)
    ? pageContent.event.details
    : [];
  const guestsHeading = pageContent.event?.guestsHeading ?? "";
  const guests = Array.isArray(pageContent.event?.guests)
    ? pageContent.event.guests
    : [];
  const winnersHeading = pageContent.winners?.heading ?? "";
  const winnersTable = pageContent.winners?.table ?? { columns: [], rows: [] };
  const normalizedColumns = Array.isArray(winnersTable.columns)
    ? winnersTable.columns.map((column) =>
        typeof column === "string" ? column : "",
      )
    : [];
  const videoHeading = pageContent.video?.heading ?? "";
  const youtubeId =
    typeof pageContent.video?.youtubeId === "string"
      ? pageContent.video.youtubeId.trim()
      : "";
  const gallery = Array.isArray(pageContent.gallery) ? pageContent.gallery : [];

  const galleryImageSrc = (src: string) => {
    const normalised = src.startsWith("/") ? src.slice(1) : src;
    return `${import.meta.env.BASE_URL}${normalised}`;
  };

  const renderGuest = (guest: string) => {
    const phrase = NON_BREAKING_GUEST_PHRASES.find((item) => guest.includes(item));

    if (!phrase) {
      return guest;
    }

    const [before, after] = guest.split(phrase);

    return (
      <>
        {before}
        <span className="quiz-final__guest-nowrap">{phrase}</span>
        {after}
      </>
    );
  };

  const hasEventDetails = eventDetails.length > 0;
  const hasGuests = guests.length > 0;
  const hasWinners = normalizedColumns.length > 0 && winnersTable.rows.length > 0;
  const hasVideo = Boolean(youtubeId);
  const hasGallery = gallery.length > 0;
  const hasStackContent =
    hasEventDetails || hasGuests || hasWinners || hasVideo || hasGallery;

  return (
    <article className="page page--final quiz-final">
      <div className="quiz-final__content">
        {isEnglish ? (
          <>
            {intro ? <p className="quiz-final__intro">{intro}</p> : null}
            <div className="quiz-final__link-wrapper">
              <Link
                to={chinesePageHref}
                className="quiz-final__button"
                aria-label={englishButtonAlt}
              >
                <img src={englishButtonImage} alt={englishButtonAlt} />
              </Link>
            </div>
            <div className="quiz-final__corner-graphic">
              <img src={cornerGraphicSrc} alt={cornerGraphicAlt} />
            </div>
          </>
        ) : (
          <>
            {hasStackContent ? (
              <div className="quiz-final__stack">
                {hasEventDetails ? (
                  <section className="quiz-final__section">
                    {intro ? (
                      <h2 className="quiz-final__subheading">{intro}</h2>
                    ) : null}
                    <ul className="quiz-final__details">
                      {eventDetails.map((item) => (
                        <li key={item.label} className="quiz-final__details-item">
                          <span className="quiz-final__details-label">
                            {item.label}：
                          </span>
                          <span className="quiz-final__details-value">
                            {item.value}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                {hasGuests ? (
                  <section className="quiz-final__section">
                    {guestsHeading ? (
                      <h2 className="quiz-final__subheading">{guestsHeading}</h2>
                    ) : null}
                    <ul className="quiz-final__guest-list">
                      {guests.map((guest) => (
                        <li key={guest} className="quiz-final__guest-item">
                          {renderGuest(guest)}
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                {hasWinners ? (
                  <section className="quiz-final__section">
                    {winnersHeading ? (
                      <h2 className="quiz-final__subheading">{winnersHeading}</h2>
                    ) : null}
                    <div className="quiz-final__table-wrapper">
                      <table className="quiz-final__table">
                        <thead>
                          <tr>
                            {normalizedColumns.map((column, index) => (
                              <th key={`${index}-${column}`} scope="col">
                                {column}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {winnersTable.rows.map((row, rowIndex) => (
                            <tr key={`winner-row-${rowIndex}`}>
                              {row.map((cell, cellIndex) => (
                                <td key={`winner-cell-${rowIndex}-${cellIndex}`}>
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ) : null}

                {hasVideo ? (
                  <section className="quiz-final__section">
                    {videoHeading ? (
                      <h2 className="quiz-final__subheading">{videoHeading}</h2>
                    ) : null}
                    <div className="quiz-final__video-embed">
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${youtubeId}`}
                        title={videoHeading || "YouTube video"}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </div>
                  </section>
                ) : null}

                {hasGallery ? (
                  <section className="quiz-final__section">
                    <div className="quiz-final__gallery">
                      {gallery.map((item) => (
                        <figure key={item.src} className="quiz-final__figure">
                          <img
                            src={galleryImageSrc(item.src)}
                            alt={item.alt}
                            className="quiz-final__image"
                            loading="lazy"
                            decoding="async"
                            width={1200}
                            height={800}
                          />
                          <figcaption className="quiz-final__caption">
                            {item.caption}
                          </figcaption>
                        </figure>
                      ))}
                    </div>
                  </section>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </article>
  );
};
